import { useState, useEffect } from 'react'
import { Apple, Calendar as CalendarIcon, Sparkles, X, Copy, Activity, Timer, MapPin, Flame, Settings, ShieldAlert } from 'lucide-react'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import toast from 'react-hot-toast'
import api from '../../../services/api'

const MEAL_TIMES = {
    breakfast: '🌅 Petit-déjeuner',
    lunch: '☀️ Déjeuner',
    snack: '🍎 Goûter',
    dinner: '🌙 Dîner'
}

export default function PatientMealLogsTab({ patient, onChange }) {
    const patientId = patient.id
    const [subTab, setSubTab] = useState('timeline') // 'timeline' ou 'settings'
    const [timeline, setTimeline] = useState([ ]) // attention espace
    const [goals, setGoals] = useState({ }) // attention espace
    const [loading, setLoading] = useState(true)

    // États de l'IA
    const [isAiModalOpen, setIsAiModalOpen] = useState(false)
    const [aiSummary, setAiSummary] = useState("")
    const [isGenerating, setIsGenerating] = useState(false)

    // État d'enregistrement de configuration
    const [savingSettings, setSavingSettings] = useState(false)

    useEffect(() => {
        api.get(`/api/patients/${patientId}/meals`)
            .then(res => {
                const combined = [...(res.data.logs || [ ]), ...(res.data.activities || [ ])] // attention espace
                combined.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                setTimeline(combined)
                setGoals(res.data.goals) 
            })
            .catch(err => toast.error("Erreur de chargement de la timeline"))
            .finally(() => setLoading(false))
    }, [patientId])

    // Sauvegarde en direct des droits d'accès cliniques du patient
    const handleToggleFeature = async (field, value) => {
        setSavingSettings(true)
        try {
            await api.patch(`/api/patients/${patientId}`, { [field]: value })
            toast.success("Droits d'accès mis à jour")
            if (onChange) onChange() // Rafraîchit l'objet patient du parent
        } catch (e) {
            toast.error("Erreur de sauvegarde")
        } finally {
            setSavingSettings(false)
        }
    }



    const generateSummary = async () => {
        setIsAiModalOpen(true)
        setAiSummary("")
        setIsGenerating(true)
        
        try {
            const token = localStorage.getItem('token')
            const response = await fetch(`/api/ai/patient/${patientId}/diary-summary`, {
                headers: { 'Authorization': `Bearer ${token}` }
            })

            if (!response.ok) {
                // On extrait le message du Backend !
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.detail || "Erreur serveur");
            }

            const reader = response.body.getReader()
            const decoder = new TextDecoder("utf-8")
            let done = false
            let fullText = ""

            while (!done) {
                const { value, done: readerDone } = await reader.read()
                done = readerDone
                if (value) {
                    const chunk = decoder.decode(value, { stream: true })
                    fullText += chunk
                    setAiSummary(fullText)
                }
            }
        } catch (error) {
            toast.error(error.message || "Erreur de communication avec l'IA")
            setIsAiModalOpen(false) // On referme la modale si l'accès est bloqué !
        } finally {
            setIsGenerating(false)
        }
    }


    if (loading) return <div className="p-12 flex justify-center"><div className="spinner border-sage-600"/></div>

    // Grouper les items par date pour l'affichage
    const groupedTimeline = timeline.reduce((acc, item) => {
        if (!acc[item.date]) acc[item.date] = []
        acc[item.date].push(item)
        return acc
    }, {}) 

    return (
        <div className="space-y-6">
            {/* Local Switcher d'onglet décentralisé (Contenu à gauche, Configuration à l'extrême droite) */}
            <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
                <div className="flex gap-2">
                    <button 
                        type="button"
                        onClick={() => setSubTab('timeline')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${
                            subTab === 'timeline' 
                                ? 'bg-sage-600 text-white border-sage-600 shadow-sm' 
                                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                        }`}
                    >
                        📊 Timeline Journal
                    </button>
                    {/* Les futurs onglets de contenus (Calculateurs, etc.) viendront s'aligner ici à gauche */}
                </div>

                <button 
                    type="button"
                    onClick={() => setSubTab('settings')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border flex items-center gap-1.5 ${
                        subTab === 'settings' 
                            ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm' 
                            : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                    }`}
                >
                    <Settings className="w-3.5 h-3.5" /> Réglages Portail
                </button>
            </div>

            {subTab === 'settings' ? (
                /* VUE : REGLAGES DU PORTAIL DU PATIENT */
                <div className="space-y-6 animate-in fade-in zoom-in-95 max-w-xl">
                    <div className="bg-indigo-50 border border-indigo-100 p-5 rounded-2xl flex gap-4 items-start mb-6">
                        <div className="p-2 bg-indigo-100 rounded-lg text-indigo-600 shrink-0">
                            <ShieldAlert className="w-5 h-5" />
                        </div>
                        <div>
                            <h4 className="font-bold text-indigo-900 text-sm">Droits Thérapeutiques Individuels</h4>
                            <p className="text-xs text-indigo-800 mt-1 leading-relaxed">
                                Les modifications apportées ici s'appliquent immédiatement et uniquement au portail de <strong>{patient.first_name} {patient.last_name}</strong>. Utile pour modérer le suivi de patients sensibles (ex: profils TCA).
                            </p>
                        </div>
                    </div>

                    <div className="space-y-3 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                        <label className="flex items-center gap-4 cursor-pointer">
                            <input 
                                type="checkbox" 
                                disabled={savingSettings}
                                checked={patient.portal_show_weight !== false} 
                                onChange={e => handleToggleFeature('portal_show_weight', e.target.checked)} 
                                className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500 border-gray-300"
                            />
                            <div className="flex-1">
                                <p className="text-sm font-bold text-gray-800">Autoriser l'accès au poids</p>
                                <p className="text-xs text-gray-500 mt-0.5">Le patient pourra ajouter son poids et visualiser sa courbe d'évolution.</p>
                            </div>
                        </label>

                        <label className="flex items-center gap-4 cursor-pointer border-t border-gray-100 pt-4 mt-4">
                            <input 
                                type="checkbox" 
                                disabled={savingSettings}
                                checked={patient.portal_show_meals !== false} 
                                onChange={e => handleToggleFeature('portal_show_meals', e.target.checked)} 
                                className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500 border-gray-300"
                            />
                            <div className="flex-1">
                                <p className="text-sm font-bold text-gray-800">Autoriser le carnet alimentaire</p>
                                <p className="text-xs text-gray-500 mt-0.5">Le patient pourra remplir son journal de repas et visualiser le plan alimentaire actif.</p>
                            </div>
                        </label>

                        <label className="flex items-center gap-4 cursor-pointer border-t border-gray-100 pt-4 mt-4">
                            <input 
                                type="checkbox" 
                                disabled={savingSettings}
                                checked={patient.portal_show_activity !== false} 
                                onChange={e => handleToggleFeature('portal_show_activity', e.target.checked)} 
                                className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500 border-gray-300"
                            />
                            <div className="flex-1">
                                <p className="text-sm font-bold text-gray-800">Autoriser le carnet d'activité physique</p>
                                <p className="text-xs text-gray-500 mt-0.5">Le patient pourra enregistrer ses séances d'efforts et d'entraînements.</p>
                            </div>
                        </label>
                    </div>
                </div>
            ) : (
                /* VUE TIMELINE : JOURNAL (Rendu d'origine inchangé) */
                <div className="space-y-6">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-serif text-sage-700 flex items-center gap-2">
                            <Activity className="w-5 h-5" /> Timeline (Alimentation & Activité)
                        </h3>
                        
                        {timeline.length > 0 && (
                            <button onClick={generateSummary} className="btn bg-gradient-to-r from-purple-600 to-indigo-600 text-white flex items-center gap-2 py-2 text-sm shadow-md hover:shadow-lg transition-all hover:scale-105">
                        <Sparkles className="w-4 h-4" /> Bilan IA de la semaine
                    </button>
                )}
            
            </div>

            {timeline.length === 0 ? (
                <div className="text-center p-8 border-2 border-dashed border-gray-100 rounded-xl bg-gray-50 text-gray-400">
                    <Apple className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>Le patient n'a pas encore utilisé son carnet alimentaire.</p>
                </div>
            ) : (
                <div className="space-y-8">
                    {Object.keys(groupedTimeline).sort((a, b) => new Date(b) - new Date(a)).map(date => (
                        <div key={date} className="relative">
                            <div className="sticky top-0 z-10 flex items-center gap-3 mb-4 bg-white/90 backdrop-blur-sm py-2">
                                <div className="p-2 bg-sage-100 text-sage-700 rounded-lg">
                                    <CalendarIcon className="w-4 h-4" />
                                </div>
                                <h4 className="font-bold text-gray-800 capitalize flex items-center gap-3">
                                    {format(new Date(date), 'EEEE dd MMMM yyyy', { locale: fr })}
                                    {/* Affiche les objectifs atteints s'il y en a */}
                                    {goals[date] && goals[date].length > 0 && (
                                        <span className="text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full uppercase tracking-wider font-bold">
                                            🎯 {goals[date].length} Objectif(s) atteint(s)
                                        </span>
                                    )}
                                </h4>
                            </div>

                            {/* Liste détaillée des objectifs */}
                            {goals[date] && goals[date].length > 0 && (
                                <div className="mb-4 ml-6 pl-4 border-l-2 border-sage-100">
                                    <div className="bg-orange-50 border border-orange-100 rounded-xl p-3 inline-block">
                                        <ul className="space-y-1">
                                            {goals[date].map((axe, idx) => (
                                                <li key={idx} className="text-xs font-bold text-orange-800 flex items-center gap-2">
                                                    <span className="text-green-500 text-sm">✓</span> {axe}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-4 mt-4">
                                
                                {/* COLONNE GAUCHE : ALIMENTATION */}
                                <div className="flex flex-col gap-4 pl-4 border-l-2 border-sage-100 ml-4">
                                    {(() => {
                                        // On filtre les repas et on force l'ordre chronologique de la journée
                                        const mealOrder = { 'breakfast': 1, 'lunch': 2, 'snack': 3, 'dinner': 4 };
                                        const dayMeals = groupedTimeline[date]
                                            .filter(i => i.type === 'meal')
                                            .sort((a, b) => mealOrder[a.meal_time] - mealOrder[b.meal_time]);
                                        
                                        if (dayMeals.length === 0) return <p className="text-xs text-sage-400 italic">Aucun repas noté ce jour.</p>;

                                        return dayMeals.map(item => (
                                            <div key={`m_${item.id}`} className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow relative">
                                                <div className="absolute -left-[23px] top-6 w-3 h-3 bg-sage-400 rounded-full border-2 border-white"></div>
                                                <div className="flex justify-between items-start mb-3">
                                                    <span className="text-[10px] font-bold text-sage-700 uppercase bg-sage-50 px-2 py-1 rounded flex items-center gap-1">
                                                        <Apple className="w-3 h-3"/> {MEAL_TIMES[item.meal_time]}
                                                    </span>
                                                    {item.feeling && <span className="text-xl" title="Ressenti du patient">{item.feeling}</span>}
                                                </div>
                                                {item.image_url && (
                                                    <div className="mb-3 rounded-xl overflow-hidden bg-gray-100">
                                                        <img src={item.image_url} alt="Repas" className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500 cursor-pointer" onClick={() => window.open(item.image_url, '_blank')} />
                                                    </div>
                                                )}
                                                <p className="text-sm text-gray-700 font-medium whitespace-pre-wrap leading-relaxed">{item.food_text}</p>
                                            </div>
                                        ));
                                    })()}
                                </div>

                                {/* COLONNE DROITE : ACTIVITÉ PHYSIQUE */}
                                <div className="flex flex-col gap-4 pl-4 border-l-2 border-blue-100 ml-4">
                                    {(() => {
                                        const daySports = groupedTimeline[date].filter(i => i.type === 'sport');
                                        
                                        if (daySports.length === 0) return <p className="text-xs text-blue-400 italic">Aucune activité notée ce jour.</p>;

                                        return daySports.map(item => (
                                            <div key={`s_${item.id}`} className="bg-blue-50/30 border border-blue-100 rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow relative">
                                                <div className="absolute -left-[23px] top-6 w-3 h-3 bg-blue-400 rounded-full border-2 border-white"></div>
                                                <div className="flex justify-between items-start mb-3">
                                                    <span className="text-[10px] font-bold text-blue-700 uppercase bg-blue-100 px-2 py-1 rounded flex items-center gap-1">
                                                        <Activity className="w-3 h-3"/> {item.activity_type}
                                                    </span>
                                                    {item.feeling && <span className="text-xl" title="Ressenti du patient">{item.feeling}</span>}
                                                </div>
                                                <div className="flex flex-wrap gap-2 mb-2">
                                                    {item.duration_minutes && <span className="text-[10px] font-bold text-blue-800 bg-white border border-blue-200 px-2 py-1 rounded-lg flex items-center gap-1"><Timer className="w-3 h-3 text-blue-500"/> {item.duration_minutes} min</span>}
                                                    {item.distance_km && <span className="text-[10px] font-bold text-green-800 bg-white border border-green-200 px-2 py-1 rounded-lg flex items-center gap-1"><MapPin className="w-3 h-3 text-green-500"/> {item.distance_km} km</span>}
                                                    {item.intensity && <span className="text-[10px] font-bold text-orange-800 bg-white border border-orange-200 px-2 py-1 rounded-lg flex items-center gap-1"><Flame className="w-3 h-3 text-orange-500"/> {item.intensity}</span>}
                                                </div>
                                                {item.notes && <p className="text-sm text-gray-700 italic">"{item.notes}"</p>}
                                            </div>
                                        ));
                                    })()}
                                </div>

                            </div>
                        </div>
                    ))}
                </div>
            )}


            {isAiModalOpen && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[85vh]">
                        <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gradient-to-r from-purple-50 to-indigo-50 rounded-t-2xl">
                            <h3 className="font-serif font-bold text-xl text-purple-900 flex items-center gap-2">
                                <Sparkles className="w-5 h-5 text-purple-600"/> Analyse de Gaston
                            </h3>
                            <button onClick={() => setIsAiModalOpen(false)}><X className="w-6 h-6 text-purple-300 hover:text-purple-600" /></button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
                            <div className="bg-white border border-purple-100 rounded-xl p-5 shadow-sm min-h-[200px]">
                                {aiSummary ? (
                                    <div className="text-gray-700 whitespace-pre-wrap font-sans text-sm leading-relaxed">
                                        {aiSummary}
                                    </div>
                                ) : (
                                    <div className="h-full flex flex-col items-center justify-center text-purple-300 gap-3 py-12">
                                        <div className="spinner border-purple-400 w-8 h-8" />
                                        <p className="text-sm font-medium animate-pulse">Gaston lit le carnet du patient...</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-4 border-t border-gray-100 bg-white rounded-b-2xl flex justify-between items-center">
                            <span className="text-[10px] text-gray-400 uppercase font-bold">L'IA peut faire des erreurs.</span>
                            <div className="flex gap-2">
                                <button onClick={() => {
                                    navigator.clipboard.writeText(aiSummary)
                                    toast.success("Copié !")
                                }} disabled={isGenerating} className="btn bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 flex items-center gap-2 disabled:opacity-50">
                                    <Copy className="w-4 h-4"/> Copier
                                </button>
                                <button onClick={() => setIsAiModalOpen(false)} className="btn bg-purple-600 text-white hover:bg-purple-700 px-6">Fermer</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            </div>
            )}
        </div>
    )
}